import { ErrorCode, PortalApiError } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { TrackingEvent } from '../../model/monitoring'
import { ErrorHandler, CheckoutErrorContext } from './types'
import { checkoutService } from '@afterpaytouch/portal-api/consumer/checkout'
import { authService } from '@afterpaytouch/portal-api/consumer/auth'
import { setEligibilityRemediationAttempted, setCheckoutError, setCheckoutStatusToBeLoaded } from '../../state'
import { checkoutErrors, TerminalErrorCode } from '../../model'

export const createCheckoutStartConvergenceIneligibleErrorHandler = (): ErrorHandler => ({
  canHandle: (errorCode: ErrorCode): boolean => {
    return errorCode === ErrorCode.CashCohortConvergenceIneligible
  },

  handle: async (errorCode: ErrorCode, _error: PortalApiError, context: CheckoutErrorContext): Promise<Route> => {
    if (context.eligibilityRemediationAttempted) {
      context.logEvent(TrackingEvent.CHECKOUT_DECLINED, {
        isRecoverableTerminalError: false,
        errorCode,
      })

      context.dispatch(
        setCheckoutError({
          error: checkoutErrors[errorCode as ErrorCode] ?? TerminalErrorCode.Default,
          errorCode,
        })
      )

      return Route.TERMINAL_ERROR
    }

    context.logEvent(TrackingEvent.CHECKOUT_DECLINED, {
      isRecoverableTerminalError: true,
      errorCode,
    })

    // Mark that we've attempted remediation so we don't loop
    context.dispatch(setEligibilityRemediationAttempted(true))

    // Reset checkout and auth API state so startCheckout and cash auth are re-fetched
    context.dispatch(checkoutService.util.resetApiState())
    context.dispatch(authService.util.resetApiState())
    context.dispatch(setCheckoutStatusToBeLoaded())

    // Tell pay-later to discard the stored customer request and create a fresh one, so
    // grantly's ShadowAccountPreAuthorizationHandler re-evaluates eligibility. Awaiting
    // this here means /cash-auth's polling reads the new server state on its first poll.
    const action = context.dispatch(authService.endpoints.cash.initiate({ ...context.cashAuthArgs, forceNewRequest: true }))
    try {
      await action.unwrap()
    } catch (err: unknown) {
      const rtkErr = err as { status?: unknown; data?: { message?: string }; message?: string }
      context.logEvent(TrackingEvent.CASH_FORCE_NEW_REQUEST_FAILED, {
        errorStatus: rtkErr?.status,
        errorMessage: rtkErr?.data?.message ?? rtkErr?.message,
      })
    } finally {
      action.unsubscribe()
    }

    return context.cashAuthRoute
  },
})
